/**
 * Campi di modulo con le maschere italiane dei campi elementari ANAC.
 *
 * `CampoDato` e' il pezzo centrale: riceve un `TipoCampo` e ne ricava maschera,
 * validazione, tastiera mobile e testo di aiuto. Cosi' una pagina puo' generare
 * il form di uno schema partendo dai soli descrittori, senza un `switch` per
 * ogni pagina, e una data digitata e' `gg/mm/yyyy` in tutte e sei le pagine che
 * ne chiedono una.
 *
 * Il valore resta **sempre una stringa** e viene inviato all'API tale e quale:
 * non passa mai da `Number`, quindi «1.456.456,58» non rischia di tornare come
 * `1456456.5799999` e non c'e' nessuna riformattazione lato client (CONVENTIONS
 * §2: la serializzazione italiana e' competenza esclusiva dell'API).
 */

import { useId, useState } from "react";
import type { ReactNode } from "react";
import type { TipoCampo } from "../api/types";
import {
  mascheraAnno,
  mascheraCifraSingola,
  mascheraCodiceFiscaleEnte,
  mascheraData,
  mascheraDataOra,
  mascheraImporto,
  mascheraOra,
  mascheraPercentuale,
  normalizzaImporto,
  validaAnno,
  validaCodiceFiscaleEnte,
  validaData,
  validaDataOra,
  validaEmail,
  validaImporto,
  validaOra,
  validaPercentuale,
  validaSemestre,
  validaTrimestre,
  validaUrl,
} from "../lib/formats";
import type { EsitoValidazione } from "../lib/formats";

// ------------------------------------------------------- involucro comune -- //

interface ProprietaInvolucro {
  etichetta: string;
  obbligatorio?: boolean;
  aiuto?: string;
  errore?: string | null;
  idControllo: string;
  children: ReactNode;
}

/** Identificativo del paragrafo di aiuto di un controllo. */
const idAiutoDi = (idControllo: string) => `${idControllo}-aiuto`;

/** Identificativo del paragrafo di errore di un controllo. */
const idErroreDi = (idControllo: string) => `${idControllo}-errore`;

/**
 * Valore di `aria-describedby` da mettere sul controllo.
 *
 * Senza, uno screen reader annuncia il campo come «non valido» — via
 * `aria-invalid` — ma non legge mai il testo che dice *come* correggerlo, che e'
 * l'unica indicazione utile a chi ha appena digitato `31/02/2026` (WCAG 3.3.1 e
 * 3.3.3, requisito AgID sui moduli). L'ordine non e' casuale: l'errore va
 * annunciato per primo.
 *
 * Rispecchia la resa di `Involucro`, dove l'aiuto sparisce quando c'e' un
 * errore: puntare un paragrafo non renderizzato lascerebbe l'annuncio muto.
 */
function descrittoriDi(
  idControllo: string,
  aiuto?: string,
  errore?: string | null,
): string | undefined {
  const riferimenti: string[] = [];
  if (errore) riferimenti.push(idErroreDi(idControllo));
  else if (aiuto) riferimenti.push(idAiutoDi(idControllo));
  return riferimenti.length > 0 ? riferimenti.join(" ") : undefined;
}

function Involucro({
  etichetta,
  obbligatorio,
  aiuto,
  errore,
  idControllo,
  children,
}: ProprietaInvolucro) {
  return (
    <div className="campo">
      <label className="campo__etichetta" htmlFor={idControllo}>
        {etichetta}
        {obbligatorio ? (
          <span className="campo__obbligatorio" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {aiuto && !errore ? (
        <p className="campo__aiuto" id={idAiutoDi(idControllo)}>
          {aiuto}
        </p>
      ) : null}
      {errore ? (
        <p className="campo__errore" id={idErroreDi(idControllo)} role="alert">
          {errore}
        </p>
      ) : null}
    </div>
  );
}

// ------------------------------------------- descrizione dei tipi di campo -- //

interface RegoleTipo {
  maschera: (grezzo: string) => string;
  /** Normalizzazione all'uscita dal campo (es. i decimali di un importo). */
  normalizza?: (valore: string) => string;
  valida: (valore: string) => EsitoValidazione;
  segnaposto: string;
  /** Vero per i campi che vanno resi in carattere monospaziato. */
  tabulare: boolean;
  inputMode?: "numeric" | "decimal" | "email" | "url" | "text";
  lunghezzaMassima?: number;
}

const SENZA_MASCHERA = (grezzo: string) => grezzo;
const SEMPRE_VALIDO = (): EsitoValidazione => null;

const REGOLE: Record<TipoCampo, RegoleTipo> = {
  testo: {
    maschera: SENZA_MASCHERA,
    valida: SEMPRE_VALIDO,
    segnaposto: "",
    tabulare: false,
  },
  testo_lungo: {
    maschera: SENZA_MASCHERA,
    valida: SEMPRE_VALIDO,
    segnaposto: "",
    tabulare: false,
  },
  anno: {
    maschera: mascheraAnno,
    valida: validaAnno,
    segnaposto: "2026",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 4,
  },
  trimestre: {
    maschera: mascheraCifraSingola,
    valida: validaTrimestre,
    segnaposto: "3",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 1,
  },
  semestre: {
    maschera: mascheraCifraSingola,
    valida: validaSemestre,
    segnaposto: "1",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 1,
  },
  data: {
    maschera: mascheraData,
    valida: validaData,
    segnaposto: "gg/mm/aaaa",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 10,
  },
  ora: {
    maschera: mascheraOra,
    valida: validaOra,
    segnaposto: "hh:mm",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 5,
  },
  data_ora: {
    maschera: mascheraDataOra,
    valida: validaDataOra,
    segnaposto: "gg/mm/aaaa hh:mm",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 16,
  },
  importo: {
    maschera: mascheraImporto,
    normalizza: normalizzaImporto,
    valida: validaImporto,
    segnaposto: "1.456.456,58",
    tabulare: true,
    inputMode: "decimal",
  },
  percentuale: {
    maschera: mascheraPercentuale,
    valida: validaPercentuale,
    segnaposto: "75,33",
    tabulare: true,
    inputMode: "decimal",
    lunghezzaMassima: 6,
  },
  email: {
    maschera: (grezzo) => grezzo.trim(),
    valida: validaEmail,
    segnaposto: "n.cognome@dominio.it",
    tabulare: false,
    inputMode: "email",
  },
  url: {
    maschera: (grezzo) => grezzo.trim(),
    valida: validaUrl,
    segnaposto: "https://www.dominio.it/percorso",
    tabulare: false,
    inputMode: "url",
  },
  codice_fiscale_ente: {
    maschera: mascheraCodiceFiscaleEnte,
    valida: validaCodiceFiscaleEnte,
    segnaposto: "12345678901",
    tabulare: true,
    inputMode: "numeric",
    lunghezzaMassima: 11,
  },
  opzioni: {
    maschera: SENZA_MASCHERA,
    valida: SEMPRE_VALIDO,
    segnaposto: "",
    tabulare: false,
  },
};

// ------------------------------------------------------------- CampoDato -- //

export interface ProprietaCampoDato {
  etichetta: string;
  tipo: TipoCampo;
  valore: string;
  onCambia: (valore: string) => void;
  obbligatorio?: boolean;
  aiuto?: string;
  /** Errore imposto dall'esterno (per esempio da `errore.dettagli` dell'API). */
  errore?: string | null;
  disabilitato?: boolean;
  opzioni?: readonly string[];
  segnaposto?: string;
  /** Occupa l'intera larghezza in `.griglia-campi`. */
  intero?: boolean;
}

/**
 * Campo che si configura da solo a partire dal tipo elementare ANAC.
 *
 * La validazione compare quando il campo perde il fuoco, non a ogni tasto: chi
 * sta ancora digitando `30/0` non deve vedersi dire che la data e' sbagliata.
 */
export function CampoDato({
  etichetta,
  tipo,
  valore,
  onCambia,
  obbligatorio = false,
  aiuto,
  errore,
  disabilitato = false,
  opzioni,
  segnaposto,
  intero = false,
}: ProprietaCampoDato) {
  const idControllo = useId();
  const [toccato, setToccato] = useState(false);
  const regole = REGOLE[tipo];

  const erroreCalcolato =
    errore ??
    (toccato
      ? obbligatorio && !valore.trim()
        ? "Campo obbligatorio."
        : regole.valida(valore)
      : null);

  const classi = [
    "campo__controllo",
    regole.tabulare ? "campo__controllo--dati" : "",
    erroreCalcolato ? "campo__controllo--errato" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const descrittori = descrittoriDi(idControllo, aiuto, erroreCalcolato);

  const involucro = (contenuto: ReactNode) => (
    <div className={intero ? "campo--intero" : undefined}>
      <Involucro
        etichetta={etichetta}
        obbligatorio={obbligatorio}
        aiuto={aiuto}
        errore={erroreCalcolato}
        idControllo={idControllo}
      >
        {contenuto}
      </Involucro>
    </div>
  );

  if (tipo === "opzioni") {
    return involucro(
      <select
        id={idControllo}
        className={classi}
        value={valore}
        disabled={disabilitato}
        onChange={(evento) => onCambia(evento.target.value)}
        onBlur={() => setToccato(true)}
        aria-invalid={erroreCalcolato ? true : undefined}
        aria-describedby={descrittori}
      >
        <option value="">— seleziona —</option>
        {(opzioni ?? []).map((opzione) => (
          <option key={opzione} value={opzione}>
            {opzione}
          </option>
        ))}
      </select>,
    );
  }

  if (tipo === "testo_lungo") {
    return involucro(
      <textarea
        id={idControllo}
        className={classi}
        value={valore}
        disabled={disabilitato}
        placeholder={segnaposto}
        onChange={(evento) => onCambia(evento.target.value)}
        onBlur={() => setToccato(true)}
        aria-invalid={erroreCalcolato ? true : undefined}
        aria-describedby={descrittori}
      />,
    );
  }

  return involucro(
    <input
      id={idControllo}
      type="text"
      className={classi}
      value={valore}
      disabled={disabilitato}
      placeholder={segnaposto ?? regole.segnaposto}
      inputMode={regole.inputMode}
      maxLength={regole.lunghezzaMassima}
      autoComplete="off"
      onChange={(evento) => onCambia(regole.maschera(evento.target.value))}
      onBlur={() => {
        setToccato(true);
        if (regole.normalizza && valore.trim()) onCambia(regole.normalizza(valore));
      }}
      aria-invalid={erroreCalcolato ? true : undefined}
      aria-describedby={descrittori}
    />,
  );
}

// ----------------------------------------------------------- CampoScelta -- //

export interface OpzioneScelta {
  valore: string;
  etichetta: string;
  disabilitata?: boolean;
}

/**
 * Select su un elenco chiuso di valori.
 *
 * E' il controllo obbligato per le denominazioni dei nodi: CONVENTIONS §7.8
 * vieta il campo libero perche' le denominazioni sono normativamente vincolate
 * e la difformita' e' rilevata da TrasparenzAI.
 */
export function CampoScelta({
  etichetta,
  valore,
  onCambia,
  opzioni,
  obbligatorio = false,
  aiuto,
  errore,
  disabilitato = false,
  vuoto = "— seleziona —",
  intero = false,
}: {
  etichetta: string;
  valore: string;
  onCambia: (valore: string) => void;
  opzioni: readonly OpzioneScelta[];
  obbligatorio?: boolean;
  aiuto?: string;
  errore?: string | null;
  disabilitato?: boolean;
  /** Testo della voce vuota; `null` per ometterla. */
  vuoto?: string | null;
  intero?: boolean;
}) {
  const idControllo = useId();
  const descrittori = descrittoriDi(idControllo, aiuto, errore);
  return (
    <div className={intero ? "campo--intero" : undefined}>
      <Involucro
        etichetta={etichetta}
        obbligatorio={obbligatorio}
        aiuto={aiuto}
        errore={errore}
        idControllo={idControllo}
      >
        <select
          id={idControllo}
          className={`campo__controllo${errore ? " campo__controllo--errato" : ""}`}
          value={valore}
          disabled={disabilitato}
          onChange={(evento) => onCambia(evento.target.value)}
          aria-invalid={errore ? true : undefined}
          aria-describedby={descrittori}
        >
          {vuoto === null ? null : <option value="">{vuoto}</option>}
          {opzioni.map((opzione) => (
            <option key={opzione.valore} value={opzione.valore} disabled={opzione.disabilitata}>
              {opzione.etichetta}
            </option>
          ))}
        </select>
      </Involucro>
    </div>
  );
}

// ---------------------------------------------------------- CampoLibero --- //

/** Campo di testo semplice, senza maschera: titoli, note, password. */
export function CampoLibero({
  etichetta,
  valore,
  onCambia,
  tipo = "text",
  obbligatorio = false,
  aiuto,
  errore,
  disabilitato = false,
  segnaposto,
  autoComplete,
  multilinea = false,
  intero = false,
}: {
  etichetta: string;
  valore: string;
  onCambia: (valore: string) => void;
  tipo?: "text" | "password" | "email";
  obbligatorio?: boolean;
  aiuto?: string;
  errore?: string | null;
  disabilitato?: boolean;
  segnaposto?: string;
  autoComplete?: string;
  multilinea?: boolean;
  intero?: boolean;
}) {
  const idControllo = useId();
  const classi = `campo__controllo${errore ? " campo__controllo--errato" : ""}`;
  const descrittori = descrittoriDi(idControllo, aiuto, errore);
  return (
    <div className={intero ? "campo--intero" : undefined}>
      <Involucro
        etichetta={etichetta}
        obbligatorio={obbligatorio}
        aiuto={aiuto}
        errore={errore}
        idControllo={idControllo}
      >
        {multilinea ? (
          <textarea
            id={idControllo}
            className={classi}
            value={valore}
            disabled={disabilitato}
            placeholder={segnaposto}
            onChange={(evento) => onCambia(evento.target.value)}
            aria-invalid={errore ? true : undefined}
            aria-describedby={descrittori}
          />
        ) : (
          <input
            id={idControllo}
            type={tipo}
            className={classi}
            value={valore}
            disabled={disabilitato}
            placeholder={segnaposto}
            autoComplete={autoComplete}
            onChange={(evento) => onCambia(evento.target.value)}
            aria-invalid={errore ? true : undefined}
            aria-describedby={descrittori}
          />
        )}
      </Involucro>
    </div>
  );
}
